import { Injectable } from "@angular/core";
import { DwChartConfig, DwChartPreferences, DwChartTypes } from "../models/dw-chart-preferences.model";
import { DwGridConfig, DwGridHeightMode, DwGridPreferences, DwMetaDataGridPreferences, DwMetaDataGridViewPreference, DwQuery } from "@devwareapps/devware-cap";
import { DwChartColors, DwChartColors2, DwChartColors3 } from "../models/dw-chart-colors.model";



@Injectable({ providedIn: 'root' })
export class DwChartBuilderService {

    colors = DwChartColors;

    buildChartConfig(chartPreference: DwChartPreferences, data: any[]): DwChartConfig {

        const radialChart = chartPreference.chartType === DwChartTypes.pie
            || chartPreference.chartType === DwChartTypes.doughnut
            || chartPreference.chartType === DwChartTypes.polarArea;
        const outsideValueLabels = radialChart
            && chartPreference.showValues
            && chartPreference.valueLabelPosition === 'outside';

        const chartConfig: DwChartConfig = {
            chartType: this.convertChartType(chartPreference.chartType),
            chartOptions: {
                title: {
                    display: false,
                    text: 'Checkride Types',
                    color: '#ffffff',
                    fontSize: 18,
                    position: 'top'
                },
                legend: {
                    display: !chartPreference.hideLegend,
                    position: 'bottom'
                },
                layout: {
                    padding: {
                        top: chartPreference.showValues ? 14 : 0,
                        right: chartPreference.showValues ? 34 : 0,
                        bottom: outsideValueLabels ? 14 : 0,
                        left: outsideValueLabels ? 34 : 0
                    }
                }
            },
            chartData: {
                labels: this.getDataForSeries(chartPreference.categoryField, data, chartPreference.maxItemsToShow),
                datasets: []
            }
        };

        let hasY2Axis = false;

        for (const series of chartPreference.series) {
            const dataSet: any = {
                type: this.convertChartType(series.chartType),
                label: series.legendDisplay,
                backgroundColor: series.backgroundColor || series.borderColor,
                borderColor: series.borderColor || series.backgroundColor,
                fill: false,
                valueSuffix: series.valueSuffix,
                data: this.getDataForSeries(series.dataField, data, chartPreference.maxItemsToShow)
            };

            if (series.alternateColors) {
                const palette = [...this.colors, ...DwChartColors2, ...DwChartColors3];
                const start = Math.max(0, series.alternateColorStart || 0);
                dataSet.backgroundColor = dataSet.data.map((_value: any, index: number) =>
                    palette[(index + start) % palette.length]);
            }

            if (series.axis === 'y2') {
                hasY2Axis = true; 
                dataSet.yAxisID = 'y2';
            }

            chartConfig.chartData.datasets.push(dataSet);
        }

        if (hasY2Axis) {
            chartConfig.chartOptions.scales = {
                yAxes: [{
                    type: 'linear',
                    position: 'left',
                    // options: {
                    //     ticks: {
                    //         beginAtZero: true,
                    //     }
                    // },
                    ticks: {
                        beginAtZero: true,
                    },
                    // gridLines: {
                    //     // color: 'blue'
                    // }
                },
                {
                    type: 'linear',
                    position: 'right',
                    id: 'y2',
                    ticks: {
                        beginAtZero: true,
                        key: 'Anthony-test-3'
                    },
                    gridLines: {
                        color: 'green',
                        display: false,
                    }
                }
                ]
            };
        } else {
            this.setupBarColumnSingleAxis(chartPreference, chartConfig);
        }

        if (chartPreference.showValues) {
            this.setupValueLabels(chartConfig, chartPreference);
        }

        return chartConfig;
    }

    private setupValueLabels(chartConfig: DwChartConfig, chartPreference: DwChartPreferences): void {
        chartConfig.chartOptions.animation = {
            onComplete: function(this: any) {
                const chart = this.chart;
                const context = chart.ctx;
                context.save();
                context.font = '11px Arial';
                context.fillStyle = '#172033';
                context.textBaseline = 'middle';

                chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
                    const meta = chart.getDatasetMeta(datasetIndex);
                    if (meta.hidden) {
                        return;
                    }

                    meta.data.forEach((element: any, index: number) => {
                        const value = dataset.data[index];
                        if (value === null || value === undefined || Number(value) === 0) {
                            return;
                        }

                        const model = element._model;
                        if (!model) {
                            return;
                        }

                        const displayValue = `${value}${dataset.valueSuffix || ''}`;
                        const dataSetType = dataset.type || chart.config.type;
                        const radial = dataSetType === 'pie' || dataSetType === 'doughnut' || dataSetType === 'polarArea';

                        if (radial && model.startAngle !== undefined && model.endAngle !== undefined) {
                            const angle = (model.startAngle + model.endAngle) / 2;
                            const labelPosition = chartPreference.valueLabelPosition || 'inside';
                            const innerRadius = Number(model.innerRadius) || 0;
                            const outerRadius = Number(model.outerRadius) || 0;
                            const insideRadius = innerRadius + ((outerRadius - innerRadius) * 0.58);
                            const radius = labelPosition === 'outside' ? outerRadius + 10 : insideRadius;
                            const x = model.x + Math.cos(angle) * radius;
                            const y = model.y + Math.sin(angle) * radius;

                            context.fillStyle = labelPosition === 'outside' ? '#172033' : '#ffffff';
                            context.textAlign = labelPosition === 'outside'
                                ? (Math.cos(angle) >= 0 ? 'left' : 'right')
                                : 'center';
                            context.fillText(displayValue, x, y);
                            return;
                        }

                        const horizontal = chart.config.type === 'horizontalBar' || dataset.type === 'horizontalBar';
                        context.fillStyle = '#172033';
                        context.textAlign = horizontal ? 'left' : 'center';
                        context.fillText(displayValue, horizontal ? model.x + 5 : model.x, horizontal ? model.y : model.y - 8);
                    });
                });

                context.restore();
            }
        };
    }
    setupBarColumnSingleAxis(chartPreference: DwChartPreferences, chartConfig: DwChartConfig) {

        switch(chartPreference.chartType) {
            case DwChartTypes.bar:
                chartConfig.chartOptions.scales = {
                    yAxes: [
                        {
                            ticks: {
                                beginAtZero: true,
                                max: chartPreference.valueAxisMax,
                            },
                        },
                    ]
                };
                break;
            case DwChartTypes.column:
      
                chartConfig.chartOptions.scales = {
                    xAxes: [
                        {
                            ticks: {
                                beginAtZero: true,
                                max: chartPreference.valueAxisMax,
                            },
                        },
                    ]
                };
                break;
        }

    }

    private convertChartType(chartType: DwChartTypes): string {
        switch (chartType) {
            case DwChartTypes.bar:
                return 'bar';
            case DwChartTypes.line:
                return 'line';
            case DwChartTypes.pie:
                return 'pie';
            case DwChartTypes.column:
                //return 'bar';
                return 'horizontalBar';
            case DwChartTypes.doughnut:
                return 'doughnut';
            case DwChartTypes.polarArea:
                return 'polarArea';
            case DwChartTypes.radar:
                return 'radar';
        }

    }


    buildGridPreferences(chartPreferences: DwChartPreferences, query: DwQuery): any {
        const gridPreferences = new DwMetaDataGridPreferences();

        gridPreferences.passCurrentDataSource = false;

        gridPreferences.showDisplayProperties = query?.FieldSettings?.LoadAllLookupDisplayFields;

        gridPreferences.showFooter = true;
        gridPreferences.loadTotalRowCount = true;

        gridPreferences.hideAdd = true;
        gridPreferences.hideEditButton = true;
        gridPreferences.hideDeleteButton = true;
        gridPreferences.hideRefresh = true;
        gridPreferences.hideExport = false;
        gridPreferences.showFilter = true;
        gridPreferences.showFilterOnLoad = true;
        gridPreferences.gridStyle = {
            gridHeight: '300',
            gridHeightMode: DwGridHeightMode.fixedHeight,
            gridStyleName: 'Simple',
        }

        if (!query) {
            console.warn('No query provided for grid preferences. Grid will not be able to load data without a query.');
        }

        const view: DwMetaDataGridViewPreference =
        {
            columnPreferences: [],
            actionPreferences: {},
            display: 'Default',
            filters: null,
            item: query?.RootTable,
            useCustomActions: true,
            isDefault: true,
            query: query,
            includeAllFields: true
        };

        gridPreferences.views = [view];

        if (chartPreferences.gridPreferences?.columnPreferences) {
            view.columnPreferences = chartPreferences.gridPreferences.columnPreferences.map(columnPreference => ({
                ...columnPreference,
                useFlex: columnPreference.useFlex !== false
            }));
        } else {
            // Add Columns
            view.columnPreferences.push({
                field: chartPreferences.categoryField,
                label: chartPreferences.categoryDisplay,
                width: chartPreferences.gridPreferences?.categoryColumnWidth || 200,
                columnIndex: 0,
                useFlex: true
            });

            // Add Series Field
            for (const series of chartPreferences.series) {
                view.columnPreferences.push({
                    field: series.dataField,
                    label: series.legendDisplay,
                    width: chartPreferences.gridPreferences?.aggregateColumnWidth || 100,
                    columnIndex: view.columnPreferences.length,
                    useFlex: true
                });
            }
        }

        if (chartPreferences.gridPreferences?.supplementalColumnPreferences?.length) {
            const existingFields = new Set(view.columnPreferences.map(column => column.field));

            for (const columnPreference of chartPreferences.gridPreferences.supplementalColumnPreferences) {
                if (existingFields.has(columnPreference.field)) {
                    continue;
                }

                view.columnPreferences.push({
                    ...columnPreference,
                    columnIndex: view.columnPreferences.length,
                    useFlex: columnPreference.useFlex !== false
                });
            }
        }

        return gridPreferences;
    }


    private getDataForSeries(fieldName: string, financialData: any[], maxItemsToShow: number): any[] {
        const seriesData: any[] = [];

        if (maxItemsToShow) {
            financialData = [...financialData];
            financialData.length = maxItemsToShow;
        }

        for (const data of financialData) {
            seriesData.push(data[fieldName]);
        }


        return seriesData;
    }



}
